import { useCallback, useEffect, useRef, useState } from 'react'

export const BUILDIUM_SYNC_GRACE_PERIOD_MS = 60_000

type UseBuildiumSyncGracePeriodOptions = {
  onGracePeriodEnd: () => void | Promise<void>
  rentalId: string
}

export const useBuildiumSyncGracePeriod = ({
  onGracePeriodEnd,
  rentalId
}: UseBuildiumSyncGracePeriodOptions) => {
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())
  const timeoutIdsRef = useRef<Set<number>>(new Set())

  useEffect(() => {
    const timeoutIds = timeoutIdsRef.current
    setPendingIds(new Set())

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId))
      timeoutIds.clear()
    }
  }, [rentalId])

  const trackCreatedIds = useCallback(
    (createdIds?: string[]) => {
      const ids = [...new Set((createdIds ?? []).filter(Boolean))]
      if (ids.length === 0) {
        return
      }

      setPendingIds((currentIds) => new Set([...currentIds, ...ids]))

      const timeoutId = window.setTimeout(() => {
        Promise.resolve(onGracePeriodEnd())
          .catch((error) => {
            console.error('Unable to refresh Buildium sync status', error)
          })
          .finally(() => {
            if (!timeoutIdsRef.current.delete(timeoutId)) {
              return
            }

            setPendingIds((currentIds) => {
              const nextIds = new Set(currentIds)
              ids.forEach((id) => nextIds.delete(id))
              return nextIds
            })
          })
      }, BUILDIUM_SYNC_GRACE_PERIOD_MS)

      timeoutIdsRef.current.add(timeoutId)
    },
    [onGracePeriodEnd]
  )

  const isPending = useCallback((id: string) => pendingIds.has(id), [pendingIds])

  return { isPending, trackCreatedIds }
}
